import React from "react";

import { useGetCropMixComposerCommercialMixes, useGetCropMixComposerScoreFactors, useParams, useTranslation } from "utils";

import { Flex, Icon, PageSpin, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { CommercialMixTable } from "pages/app/crop-mix-composer/components/commercialMix/CommercialMixTable.tsx";
import { TopMatchCard } from "pages/app/crop-mix-composer/components/commercialMix/TopMatchCard.tsx";
import { createTopMatchCardProps } from "pages/app/crop-mix-composer/utils/commercialMixUtils";

const BannerItem = ({ text }: { text: string }) => (
  <Flex align="center" gap={"0.5rem"}>
    <Icon iconName={"tabler/check"} />
    <Typography variant="paragraph-m">{text}</Typography>
  </Flex>
);

const CommercialMixesBanner = () => {
  const { t } = useTranslation("cropMixComposer");

  return (
    <Flex vertical={false} className="commercial-crop-mixes-banner-container">
      <div className="profile-banner-text">
        <Typography variant="h2" className="margin-bottom-100">
          {t("commercialCropMixes.banner.title")}
        </Typography>
        <>
          <BannerItem text={t("commercialCropMixes.banner.item1")} />
          <BannerItem text={t("commercialCropMixes.banner.item2")} />
          <BannerItem text={t("commercialCropMixes.banner.item3")} />
        </>
      </div>
    </Flex>
  );
};

export const CommercialCropMixesContent = ({ isDrawer }: { isDrawer?: boolean }) => {
  const { t } = useTranslation(["cropMixComposer", "practices"]);
  const { cropMixId } = useParams();
  const { data: commercialMixes, isPending, status } = useGetCropMixComposerCommercialMixes(cropMixId ?? "");
  const { selectedGoals } = useCropMixComposerContext();
  const { data: scoreFactors, isPending: isPendingScoreFactors } = useGetCropMixComposerScoreFactors();

  if (isPending) return <PageSpin />;
  if (isPendingScoreFactors) return <PageSpin />;
  if (status === "error") {
    // Todo (Alp): Add proper "No mixes" message
    return <>Could not find any commercial mixes</>;
  }

  const top3Mixes = commercialMixes.slice(0, 3);
  const remainingMixes = commercialMixes.slice(3, commercialMixes.length - 1);

  return (
    <Flex className={`commercial-crop-mixes-container ${isDrawer ? "" : "commercial-crop-mixes-drawer-body"}`} vertical align="center">
      <Flex className="container-content" align="center" vertical>
        <CommercialMixesBanner />
        <Flex className="margin-top-200" vertical gap={"1rem"}>
          <div>
            <Typography variant="paragraph-m" bold>
              {t("topMatchCard.title")}
            </Typography>
            <Typography variant="paragraph-m">{t("cropMixComposer:topMatchCard.description")}</Typography>
          </div>
          <Flex justify="space-around" gap={"1rem"}>
            {top3Mixes.map((commercialMix) => (
              <TopMatchCard key={commercialMix.id} commercialMix={createTopMatchCardProps(commercialMix, t, selectedGoals, scoreFactors)} />
            ))}
          </Flex>
          <Flex className="commercial-crop-mixes-table-container" vertical gap="1rem">
            <Typography variant="paragraph-m" bold>
              {t("cropMixComposer:commercialCropMixes.additionalMixes")}
            </Typography>
            <CommercialMixTable cropMixComposerCommercialMix={remainingMixes} />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
